import nextId from 'react-id-generator'
import { MAX_FILE_NAME, MAX_TAGS_ON_CARDS } from '../config/config'
import { format } from 'date-fns'
import { IDateInputDates } from '@aurecon-creative-technologies/styleguide'
import { ITagProps } from '../components/Tag'
import { TagTypes } from '../enums/TagTypes'
import { IGetAutomationResponse } from '../models/api/IAutomationRequest'
export const validateAlphaNumeric = (value: string): boolean => /^[a-zA-Z0-9\s]+$/.test(String(value))
export const validateAlphaNumericUnderscoreDash = (value: string): boolean => /^[a-zA-Z0-9-_]+$/.test(String(value))

export const validateSpecialCharacters = (value: string): boolean => !/^[a-zA-Z0-9\-_,.\s]+$/.test(value)
export const URL_SPLITTER_REGEX = /(https?:\/\/[^\s]+)/g
export const URL_MATCHER_REGEX = /https?:\/\/[^\s]+/
export const ID_NAME_REGEX = /\[([a-f0-9-]+)\]\(([^)]+)\)/g

export const isAureconUser = (email: string): boolean => {
  if (!email?.includes('@')) return false
  return email.split('@')[1].toUpperCase() === 'AURECONGROUP.COM'
}

export const validateUrl = (url: string): boolean => {
  try {
    const urlData = /^(?:(?:(?:https?):)?\/\/)?(?:[\w-]+\.)+[a-z]{2,20}(?:\/\S*)?$/.test(url)
    return urlData
  } catch (e) {
    console.info('Invalid url - validation failed.')
    return false
  }
}

export const getUniqueObjects = <T>(array: Array<T>, key: keyof T): T[] => {
  return array.filter((obj, i, self) => self.findIndex((obj2) => obj2[key] === obj[key]) === i)
}

export const getInternalNameFromEmail = (email: string): string => {
  const parts = email.toLowerCase().split('@')
  const nameParts = parts[0].split('.')
  const name = nameParts.map((n) => n.slice(0, 1).toUpperCase() + n.slice(1))
  return name.join(' ')
}

export interface ISyncResult<T> {
  unchanged: Array<T>
  remove: Array<T>
  create: Array<T>
}

export const arraySync = <T>(arrayOne: Array<T>, arrayTwo: Array<T>, key: keyof T): ISyncResult<T> => {
  const result: ISyncResult<T> = {
    unchanged: [],
    remove: [],
    create: [],
  }

  // Items to remove
  arrayOne.forEach((a1) => {
    let remove = true
    arrayTwo.forEach((a2) => {
      if (a1[key] === a2[key]) remove = false
    })

    if (remove) result.remove.push(a1)
    else result.unchanged.push(a1)
  })

  // Items to create
  arrayTwo.forEach((a2) => {
    let create = true
    arrayOne.forEach((a1) => {
      if (a1[key] === a2[key]) create = false
    })

    if (create) result.create.push(a2)
  })

  return result
}

export interface IDifferenceResult<T> {
  unchanged: Array<T>
  changed: Array<T>
}

export const findDifference = <T>(
  arrayOne: Array<T>,
  arrayTwo: Array<T>,
  uniqueKey: keyof T,
  diffKey: keyof T,
): IDifferenceResult<T> => {
  const result: IDifferenceResult<T> = {
    unchanged: [],
    changed: [],
  }

  for (const arrayOneValue of arrayOne) {
    const match = arrayTwo.find((element) => arrayOneValue[uniqueKey] === element[uniqueKey])
    if (match) {
      if (arrayOneValue[diffKey] === match[diffKey]) result.unchanged.push(arrayOneValue)
      else result.changed.push(match)
    } else result.unchanged.push(arrayOneValue)
  }

  return result
}

export const clamp = (value: number, min: number, max: number): number => {
  return Math.min(max, Math.max(min, value))
}

export const unixDateToString = (time: number): string => {
  const dateObject = new Date(Number(time))
  return dateObject.toLocaleString('en-AU', { year: 'numeric', month: 'numeric', day: 'numeric' })
}

export const unixDateTimeToString = (time: number): string => {
  const dateObject = new Date(Number(time))
  return dateObject.toLocaleString('en-AU', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour12: true,
    hour: 'numeric',
    minute: 'numeric',
  })
}

export const truncateFileName = (name: string): string => {
  const temp = name.split('.')
  const filename = temp.slice(0, -1).join('.').slice(0, MAX_FILE_NAME)
  return `${filename}.${temp[temp.length - 1]}`
}

export const getReadableSize = (size: number | string): string => {
  let fileSizeIndex = 0
  let calculatedSize = Number(size)

  while (calculatedSize > 1024) {
    calculatedSize = calculatedSize / 1024
    fileSizeIndex++
  }

  return `${calculatedSize.toFixed(2)} ${fileSizes[fileSizeIndex]}`
}

const fileSizes = ['B', 'KB', 'MB', 'GB', 'TB']

export const IsNullOrUndefinedString = (value: string): boolean => {
  return (
    value === null ||
    value.toLowerCase() === 'null' ||
    value === undefined ||
    value.toLowerCase() === 'undefined' ||
    value.trim() === ''
  )
}

export const dateTimeFormat = 'dd/MM/yyyy HH:mm:ss'

export const DATE_TIME_FORMAT_WITH_MERIDIEM = 'dd/MM/yyyy hh:mm a'

export const dateToString = (value: Date | string | number, dateFormat?: string): string => {
  try {
    return format(value instanceof Date ? value : new Date(value), dateFormat ?? 'dd/MM/yyyy')
  } catch (error) {
    return ''
  }
}

export const titleToUrl = (value: string): string => {
  return value.toLowerCase().replace(/\s/g, '-')
}

export const sizeOf = function (bytes: number): string {
  if (bytes === 0) {
    return '0.00 B'
  }
  const e = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, e)).toFixed(2)} ${' KMGTP'.charAt(e)}B`
}

export const findFirstDiff = (str1: string, str2: string): number =>
  [...str1].findIndex((el, index) => el !== str2[index])

export type ISortable = string | number | Date // | null
export type ISortDirection = 'desc' | 'asc' | 'none' | undefined
interface ISortKey<T extends Record<string | number | symbol, unknown>, K extends keyof T, V extends ISortable> {
  sortKey: K
  direction?: ISortDirection
  convert?: (val: unknown) => V
}

export const compare = <T>(a: T, b: T, dir?: ISortDirection, convert?: (val: unknown) => ISortable): number => {
  if (dir === 'none') return 0
  const sortVal = dir === 'desc' ? -1 : 1
  if (convert) {
    if (convert(a) > convert(b)) return sortVal
    if (convert(a) < convert(b)) return -sortVal
  } else {
    if (a > b) return sortVal
    if (a < b) return -sortVal
  }
  return 0
}
// eslint-disable-next-line
export const sorting = <T extends Record<string | number | symbol, any>, K extends keyof T, V extends ISortable>(
  a: T | null,
  b: T | null,
  keys: ISortKey<T, K, V>[],
): number => {
  return keys.reduce(
    (p, k) => (!p ? compare((a && a[k.sortKey]) || '', (b && b[k.sortKey]) || '', k.direction, k.convert) : p),
    0,
  )
}

export const truncateString = (str: string, length: number): string => {
  if (str.length > length) {
    return `${str.substring(0, length)}...`
  }
  return str
}

export const getTagLabel = (props: { label: string; currentIndex: number; tagsLength: number; isDesktop: boolean }) => {
  const truncatedLabel = props.isDesktop ? `${props.label}` : truncateString(props.label, 16)
  return props.currentIndex === 0 && props.tagsLength > MAX_TAGS_ON_CARDS
    ? `${truncatedLabel} +${props.tagsLength - MAX_TAGS_ON_CARDS}`
    : truncateString(props.label, 17)
}

export const handleHorizantalScroll = (props: {
  element: HTMLDivElement
  speed?: number
  distance: number
  direction: string
}) => {
  const step = props.direction === 'left' ? -5 : 5
  let scrollAmount = 0
  const slideTimer = setInterval(() => {
    props.element.scrollLeft += step
    scrollAmount += Math.abs(step)
    if (scrollAmount >= props.distance) {
      clearInterval(slideTimer)
    }
  }, props.speed ?? 10)
}

//Generates keys for objects that don't have a unique GUID
export const indexKey = () => nextId()

export const trimProtocol = (url: string) => {
  return url.replace(/(^\w+:|^)\/\//, '')
}

export const htmlEntitiesParser = (text: string) => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(text, 'text/html')
  const originalText = doc.body.textContent

  return originalText
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getNestedValue = (obj: any, key: string): string => {
  const keys = key.split('.')
  let value = obj
  for (const k of keys) {
    value = value?.[k]
  }
  return value?.toString() ?? ''
}

export const sortArray = <T>(array: T[], sortKey: string, sortOrder: string): T[] => {
  return array.sort((a, b) => {
    const sort = sortOrder === 'asc' ? 1 : -1
    const aSortKey = getNestedValue(a, sortKey)
    const bSortKey = getNestedValue(b, sortKey)
    return aSortKey.localeCompare(bSortKey) * sort
  })
}

export const filterArrayByDate = <T>(array: T[], dateFilter: IDateInputDates, key: keyof T): T[] => {
  return array.filter((item) => {
    const itemKey = item[key] as string | number | Date
    return (
      (!dateFilter.startDate || new Date(itemKey) >= new Date(dateFilter.startDate)) &&
      (!dateFilter.endDate || new Date(itemKey) <= new Date(dateFilter.endDate))
    )
  })
}

export const convertToLocalISOString = (date: Date): string => {
  const offset = date.getTimezoneOffset() * 60000
  const adjustedDate = new Date(date.getTime() - offset)
  return adjustedDate.toISOString()
}

export const mapAutomationCapabilitiesToTag = (automation: IGetAutomationResponse) => {
  const capabilities: ITagProps[] =
    automation.AutomationCapabilities?.map((capability) => ({
      id: capability.Id.toString(),
      type: TagTypes.CAPABILITY,
      label: capability.Name.toString(),
    })) ?? []

  return capabilities
}

export const mapAutomationSourcesToTag = (automation: IGetAutomationResponse) => {
  const sources: ITagProps[] =
    automation.AutomationSources?.map((source) => ({
      id: source.Id.toString(),
      type: TagTypes.SOURCE,
      label: source.Name.toString(),
    })) ?? []

  return sources
}

const parser = new DOMParser()
export const getTextFromHTML = (html: string) => {
  return parser.parseFromString(html, 'text/html')?.body?.textContent ?? ''
}
